import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { FixedSizeList } from 'react-window';
import { Task } from 'api/tasks';
import { Loading } from 'components/atoms/loading';
import { TaskName } from 'components/organisms/templates/commons/name-field';
import { useCKMentionEvents } from 'hooks/use-ck-mention-events';
import { MentionType } from 'vendor/ckeditor/plugins/mention/types';
import { SuggestionComponentProps } from './mention-popup-content';
import { useMentions } from './mention-store';
import { SuggestionListItem } from './suggestion-list-item';
import { isItemMatching } from './utils';
import {
  useSelectFirstSuggestion,
  useSelectSuggestion,
} from './utils/selectSuggestion';

interface TasksMentionProps extends SuggestionComponentProps {
  data?: Task[];
  isLoading?: boolean;
}

export const TasksSuggestions: FC<TasksMentionProps> = ({
  onSelect,
  query,
  activeTab,
  data,
  isLoading = false,
}) => {
  const { pathname } = useLocation();
  const isTemplate = pathname.includes('templates');

  const filteredTasks = (data ?? [])
    .filter((task) =>
      isItemMatching(`${task.taskName} ${task.taskDescription}`, query),
    )
    .slice(0, 100);
  const [contextState] = useMentions();
  const { activeSuggestion, editor } = contextState;
  const selectSuggestion = useSelectSuggestion(activeTab);
  const selectFirstSuggestion = useSelectFirstSuggestion(activeTab);
  const suggestionArgs = useMemo(() => {
    return {
      data: filteredTasks,
      type: MentionType.Task,
      name: 'taskName',
      id: 'taskId',
    };
  }, [filteredTasks]);

  useEffect(() => {
    selectFirstSuggestion(suggestionArgs);
    // Run on strict conditions only for performance reasons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (
      activeTab === MentionType.Task &&
      activeSuggestion?.type !== MentionType.Task
    ) {
      selectSuggestion({
        direction: 1,
        ...suggestionArgs,
      });
    }
  }, [suggestionArgs, selectSuggestion, activeTab, activeSuggestion]);

  const handleCKMentionEvents = useCallback(
    (e) => {
      if (e.name === 'mentionPopup:selectNext') {
        selectSuggestion({
          direction: 1,
          ...suggestionArgs,
        });
      }
      if (e.name === 'mentionPopup:selectPrevious') {
        selectSuggestion({
          direction: -1,
          ...suggestionArgs,
        });
      }
    },
    [selectSuggestion, suggestionArgs],
  );

  useCKMentionEvents(handleCKMentionEvents, editor);

  return (
    <div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <FixedSizeList
            itemCount={filteredTasks.length}
            itemSize={40}
            height={258}
            width="100%"
          >
            {({ index, style }) => {
              const task = filteredTasks[index];

              return (
                <div style={style}>
                  <SuggestionListItem
                    key={index}
                    name={(task as Task).taskName}
                    nameEle={
                      /**
                       * task name contains html in templates,
                       * wont be in the case of real projects
                       */
                      isTemplate ? (
                        <TaskName taskName={(task as Task).taskName} />
                      ) : undefined
                    }
                    avatar={<CheckmarkOutline16 />}
                    objectId={(task as Task).taskId}
                    type={MentionType.Task}
                    onSelect={onSelect}
                  />
                </div>
              );
            }}
          </FixedSizeList>
        </>
      )}
    </div>
  );
};
